// New-habit bottom sheet. Persists via useCreateHabit (optimistic —
// the habit shows up on the grid before the network settles).

import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { ACCENTS, resolveAccentColor } from '../lib/constants.js';
import { useTheme } from '../lib/themeStore.js';
import { useCreateHabit } from '../lib/hooks.js';
import { containsSlur, BLOCKED_MESSAGE } from '../lib/safety.js';
import { sfx } from '../lib/sfx.js';

const ICONS = ['target','dumbbell','book','brain','flower','footprints','sparkles','flame','moon','star'];
const FREQS = ['Daily','Weekdays','Custom'];

export function AddHabitSheet({ open, onClose }) {
  const createHabit = useCreateHabit();

  const [name, setName] = useState('');
  const [icon, setIcon] = useState('target');
  const [accent, setAccent] = useState('blue');
  const [freq, setFreq] = useState('Daily');
  const [customDays, setCustomDays] = useState([1, 2, 3, 4, 5]);
  const [err, setErr] = useState('');
  const theme = useTheme();

  // Reset on close.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setName(''); setIcon('target'); setAccent('blue'); setFreq('Daily');
        setCustomDays([1, 2, 3, 4, 5]); setErr('');
      }, 320);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Clear the error as soon as the user stops using blocked text.
  useEffect(() => {
    if (!err) return;
    if (!containsSlur(name)) setErr('');
  }, [name]); // eslint-disable-line

  const save = () => {
    if (!name.trim()) return;
    if (containsSlur(name)) { setErr(BLOCKED_MESSAGE); return; }
    if (freq === 'Custom' && customDays.length === 0) return;
    sfx.check && sfx.check();
    createHabit.mutate({
      name: name.trim(),
      icon,
      accent,
      frequency: freq,
      customDays: freq === 'Custom' ? [...customDays].sort((a, b) => a - b) : null,
    });
    onClose();
  };

  // Resolves through the active theme so on Girlboss the "Electric" chip +
  // save-preview render hot pink (matching what will actually appear on the
  // Dashboard and Analytics tabs), and on Pro Dark/Light they render as the
  // editorial blue those themes are tuned to. Keeps the palette picker
  // honest with what the user will see after saving.
  const accentColor = resolveAccentColor(accent, theme);
  const blocked = !!err || containsSlur(name);
  const needsDays = freq === 'Custom' && customDays.length === 0;
  const canSave = !!name.trim() && !blocked && !needsDays;

  return (
    <>
      <div className={'sheet-backdrop' + (open ? ' on' : '')} onClick={onClose}/>
      <div className={'sheet' + (open ? ' on' : '')}>
        <div className="sheet-grabber"/>
        <div style={{ padding: '14px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--fg-3)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: 0 }}>
            Cancel
          </button>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--fg-1)' }}>New habit</div>
          <button onClick={save} disabled={!canSave} style={{
            background: 'transparent', border: 'none',
            color: canSave ? 'var(--accent)' : 'var(--fg-4)',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14,
            cursor: canSave ? 'pointer' : 'not-allowed', padding: 0,
          }}>Save</button>
        </div>

        <div style={{ padding: '6px 20px 28px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Preview */}
          <div style={{
            padding: 14, borderRadius: 16,
            background: `linear-gradient(180deg, ${accentColor}14, transparent)`,
            border: '1px solid var(--stroke-1)',
            display: 'flex', gap: 12, alignItems: 'center',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11,
              background: `linear-gradient(135deg, ${accentColor}35, ${accentColor}10)`,
              border: `1px solid ${accentColor}55`,
              display: 'grid', placeItems: 'center',
              boxShadow: `0 0 14px ${accentColor}44`,
            }}>
              <Icon name={icon} size={18} color={accentColor}/>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
                {name || 'Habit name'}
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
                {freq} · Midnight reset
              </div>
            </div>
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Name</div>
            <input
              className="input"
              placeholder="e.g. Write 500 words"
              value={name}
              onChange={e => setName(e.target.value)}
              style={err ? { borderColor: '#ff6b6b' } : undefined}
              aria-invalid={err ? 'true' : 'false'}
            />
            {err && (
              <div style={{ marginTop: 8, fontFamily: 'var(--font-body)', fontSize: 12, color: '#ff8a8a', lineHeight: 1.4 }}>{err}</div>
            )}
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Icon</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }}>
              {ICONS.map(n => (
                <button key={n} onClick={() => setIcon(n)} style={{
                  aspectRatio: '1', borderRadius: 10,
                  background: icon === n ? `linear-gradient(135deg, ${accentColor}35, ${accentColor}10)` : 'rgba(12,13,15,0.6)',
                  border: icon === n ? `1px solid ${accentColor}80` : '1px solid var(--stroke-1)',
                  cursor: 'pointer', display: 'grid', placeItems: 'center',
                  boxShadow: icon === n ? `0 0 10px ${accentColor}55` : 'none',
                  transition: 'all 160ms var(--ease-out)',
                }}>
                  <Icon name={n} size={16} color={icon === n ? accentColor : '#B9BDC7'}/>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Frequency</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {FREQS.map(f => (
                <button key={f} onClick={() => setFreq(f)} className={'chip-opt' + (freq === f ? ' on' : '')} style={{ flex: 1 }}>
                  {f}
                </button>
              ))}
            </div>
            {freq === 'Custom' && (() => {
              const DAY_LABELS = ['S','M','T','W','T','F','S'];
              const DAY_FULL = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
              const toggle = (i) => {
                sfx.check && sfx.check();
                setCustomDays(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
              };
              const label = customDays.length === 0
                ? 'Pick at least one day'
                : customDays.length === 7
                  ? 'Every day'
                  : [...customDays].sort((a,b)=>a-b).map(i => DAY_FULL[i]).join(' · ');
              return (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {DAY_LABELS.map((lbl, i) => {
                      const on = customDays.includes(i);
                      return (
                        <button
                          key={i}
                          onClick={() => toggle(i)}
                          aria-pressed={on}
                          aria-label={DAY_FULL[i]}
                          style={{
                            flex: 1, height: 36,
                            borderRadius: 10,
                            background: on
                              ? `linear-gradient(180deg, ${accentColor}33, ${accentColor}14)`
                              : 'rgba(255,255,255,0.03)',
                            border: on
                              ? `1px solid ${accentColor}66`
                              : '1px solid var(--stroke-1)',
                            color: on ? '#fff' : 'var(--fg-3)',
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700, fontSize: 13,
                            letterSpacing: '0.02em',
                            cursor: 'pointer',
                            transition: 'all 140ms var(--ease-out)',
                            boxShadow: on ? `0 0 10px ${accentColor}44, inset 0 1px 0 rgba(255,255,255,0.1)` : 'none',
                          }}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{
                    marginTop: 8,
                    fontFamily: 'var(--font-body)', fontSize: 11.5,
                    color: customDays.length === 0 ? '#ff8a8a' : 'var(--fg-4)',
                    lineHeight: 1.4,
                  }}>{label}</div>
                </div>
              );
            })()}
          </div>

          <div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Accent</div>
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.keys(ACCENTS).map(k => {
                // Each chip previews the actual color the habit will get
                // once saved, which depends on the active theme. Electric
                // becomes hot pink under Girlboss, deep blue under Pro Dark,
                // lighter blue under Pro Light — see resolveAccentColor.
                const c = resolveAccentColor(k, theme);
                return (
                  <div
                    key={k}
                    className={'color-chip' + (accent === k ? ' on' : '')}
                    onClick={() => setAccent(k)}
                    style={{
                      background: `linear-gradient(135deg, ${c}, ${c}AA)`,
                      boxShadow: accent === k ? `0 0 14px ${c}99` : 'none',
                    }}
                    title={ACCENTS[k].name}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
